"use client";

import { MapPinIcon } from "@heroicons/react/24/outline";
import { useState, useRef, useEffect, FC } from "react";
import toast from "react-hot-toast";
import { useData } from "data/data-provider";
import { searchLocations } from "api/locations/locations";
import LocationModal from "./LocationModal";
import { LocationListingModal } from "models/LocationModal";
import ClearDataButton from "./ClearDataButton";

export interface LocationBranchInputProps {
  ourLocation: LocationListingModal[];
  placeHolder?: string;
  desc?: string;
  className?: string;
  divHideVerticalLineClass?: string;
  autoFocus?: boolean;
  sameDropOffLocation?: boolean;
  searchBar?: boolean;
}

const LocationBranchInput: FC<LocationBranchInputProps> = ({
  autoFocus = false,
  placeHolder = "Location",
  desc = "Where are you going?",
  className = "nc-flex-1.5",
  divHideVerticalLineClass = "left-10 -right-0.5",
  sameDropOffLocation,
  searchBar,
  ourLocation,
}) => {
  const {
    setPickupLocation,
    searchedPickupLoction,
    setSearchedPickupLocation,
    pickupLocation,
    ourCompany,
  } = useData();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [searchedLocations, setSearchedLocations] = useState<any>();
  const [showPopover, setShowPopover] = useState(autoFocus);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<any>(null);

  const callSearchLocationsAPI = async (input: string) => {
    try {
      const data = (await searchLocations(input)) as any;
      setSearchedLocations(data.predictions);
    } catch (error: any) {
      toast.error(error.message); // Or handle errors in a more sophisticated way
    }
  };

  useEffect(() => {
    callSearchLocationsAPI(searchedPickupLoction);
  }, [searchedPickupLoction]);

  useEffect(() => {
    setShowPopover(autoFocus);
  }, [autoFocus]);

  useEffect(() => {
    if (eventClickOutsideDiv) {
      document.removeEventListener("click", eventClickOutsideDiv);
    }
    showPopover && document.addEventListener("click", eventClickOutsideDiv);
    return () => {
      document.removeEventListener("click", eventClickOutsideDiv);
    };
  }, [showPopover]);

  useEffect(() => {
    if (showPopover && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showPopover]);

  useEffect(() => {
    if (pickupLocation === null) {
      setSearchedPickupLocation(""); // Clear state
      if (inputRef.current) {
        inputRef.current.value = ""; // Clear the input field display
      }
    }
  }, [pickupLocation, setSearchedPickupLocation]);

  const eventClickOutsideDiv = (event: MouseEvent) => {
    if (!containerRef.current) return;
    if (!showPopover || containerRef.current.contains(event.target as Node)) {
      return;
    }
    setShowPopover(false);
  };

  const handleSelectLocation = (item: any) => {
    setSearchedPickupLocation("");
    setPickupLocation(null);
    if (item.radius) {
      setSelectedLocation(item);
      setShowLocationModal(true);
      setShowPopover(false);
    } else {
      setPickupLocation(item);
      setShowPopover(false);
    }
  };

  const handleClear = () => {
    setSearchedPickupLocation("");
    setPickupLocation(null);
    setShowPopover(false);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const renderRecentSearches = () => {
    return (
      <div className="mt-2">
        {ourLocation
          .filter((item) => item.active !== false)
          .sort((a, b) => a.price - b.price)
          .map((item) => (
            <span
              onClick={() => handleSelectLocation(item)}
              key={item.id}
              className="flex px-4 sm:px-8 items-center space-x-3 sm:space-x-4 py-4 hover:bg-primary-200 text-text-color hover:text-white  cursor-pointer justify-between"
            >
              <div className="flex ">
                <span className="block text-text-color ">
                  <MapPinIcon className="h-4 sm:h-6 w-4 sm:w-6" />
                </span>
                <span className="text-left block font-medium  mx-2">
                  {item.name}
                </span>
              </div>
              <span className="block font-medium ">
                {item.price === 0 ? "Free" : "$" + item.price + "/trip"}
              </span>
            </span>
          ))}
      </div>
    );
  };

  return (
    <div className={`relative flex ${className}`} ref={containerRef}>
      <div
        onClick={() => setShowPopover(true)}
        className={`flex z-10 flex-1 relative [ nc-hero-field-padding ] flex-shrink-0 items-center space-x-3 cursor-pointer focus:outline-none text-left  ${
          showPopover ? "nc-hero-field-focused !rounded-md" : ""
        }`}
      >
        <div className="text-text-color ">
          <MapPinIcon className="w-5 h-5 lg:w-7 lg:h-7" />
        </div>
        <div className="flex-grow">
          <input
            className={`block w-full text-text-color bg-transparent cursor-pointer border-none focus:ring-0 p-0 focus:outline-none focus:placeholder-text-color xl:text-lg font-semibold placeholder-text-color  truncate `}
            placeholder={placeHolder}
            value={
              searchedPickupLoction.length > 0
                ? searchedPickupLoction
                : pickupLocation?.address
            }
            autoFocus={showPopover}
            onChange={(e) => {
              setSearchedPickupLocation(e.currentTarget.value);
            }}
            ref={inputRef}
            readOnly
          />
          <span className="block mt-0.5 text-sm text-text-color font-light ">
            <span className={`line-clamp-1 text-text-color`}>{desc}</span>
          </span>
          {(searchedPickupLoction || pickupLocation) &&
            showPopover &&
            ourLocation.filter((loc) => loc.active !== false).length > 1 && (
              <ClearDataButton onClick={handleClear} />
            )}
        </div>
      </div>

      {showPopover && (
        <div
          className={`h-8 absolute self-center top-1/2 -translate-y-1/2 z-0  ${divHideVerticalLineClass}`}
        ></div>
      )}

      {showPopover &&
        ((searchedPickupLoction && searchedLocations?.length > 0) ||
          ourLocation.filter((item) => item.active !== false).length > 0) && (
          <div className="absolute overflow-y-auto left-0 z-40 w-full min-w-[300px] sm:min-w-[500px] bg-secondary-50 top-full mt-3 py-3 sm:py-6 rounded-md shadow-xl max-h-96 ">
            {renderRecentSearches()}
          </div>
        )}
      <LocationModal
        isOpen={showLocationModal}
        selectedLocation={selectedLocation}
        onClose={() => setShowLocationModal(false)}
        ourCompany={ourCompany}
        setSearchedPickupLocation={setSearchedPickupLocation}
        setPickupLocation={setPickupLocation}
        ourLocation={ourLocation}
      />
    </div>
  );
};

export default LocationBranchInput;
